/**
 * MasterClientsList — Le REGISTRE CENTRAL (PSI Phase 8 Master-to-Expertises).
 *
 * - Sans filtre → vue Master complète (tuile "Master" du Hub).
 * - filterExpertise="fiscal"  → Base Fiscale (lecture seule, ne crée plus rien)
 * - filterExpertise="immeuble"→ Base Immeuble (lecture seule + bouton "Ajouter à cette expertise")
 *
 * Création de dossier UNIQUEMENT depuis la vue Master. Les expertises ne créent plus de
 * client — elles activent un volet sur un Master existant.
 *
 * Batch #14.221 — refactor : modals CreateMasterModal et AddToExpertiseModal extraits dans ./masterClientsList/
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Plus, Loader2, Search, FolderTree, Building2, FileText, Landmark, Star, Users, Archive, RotateCw, Briefcase, HandCoins, Hash, BookOpenCheck } from "lucide-react";
import { toast } from "@/lib/toast";
import CreateMasterModal from "./masterClientsList/CreateMasterModal";
import AddToExpertiseModal from "./masterClientsList/AddToExpertiseModal";
import { API, authH } from "@/api";


const EXPERTISE_ICON = { fiscal: FileText, immeuble: Building2, autonome: Briefcase, paie: HandCoins, tenue_livres: BookOpenCheck };
const EXPERTISE_LABEL = { fiscal: "fiscal", immeuble: "immeuble", autonome: "entrepreneurs", paie: "paie", tenue_livres: "tenue de livres" };
const EXPERTISE_COLOR = {
  paie: "bg-orange-100 text-orange-700 border-orange-200",
  fiscal: "bg-blue-100 text-blue-700 border-blue-200",
  immeuble: "bg-emerald-100 text-emerald-700 border-emerald-200",
  autonome: "bg-teal-100 text-teal-700 border-teal-200",
};

const HEADER_CONFIG = {
  fiscal: {
    icon: FileText,
    iconColor: "text-blue-600",
    title: "Expertise Fiscale",
    subtitle: "Dossiers Master avec le volet Fiscal activé. Pour ajouter un dossier ici, sélectionnez un Master existant.",
  },
  immeuble: {
    icon: Landmark,
    iconColor: "text-emerald-600",
    title: "Expertise Immeubles",
    subtitle: "Dossiers Master avec le volet Immobilier activé.",
  },
  null: {
    icon: FolderTree,
    iconColor: "text-indigo-600",
    title: "Master",
    subtitle: "Création et gestion de l'identité couple (Principal + Secondaire). Source unique de vérité.",
  },
};

// Formulaires Fiscal / Immeuble : /admin/fiscal-dossiers et /admin/immeuble-dossiers.


// =============================================================
//  LISTE PRINCIPALE
// =============================================================
export default function MasterClientsList({ filterExpertise = null }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [showAddToExp, setShowAddToExp] = useState(false);
  // Batch #14.21 — Filtre archive : "active" | "archived" | "all"
  const [archiveFilter, setArchiveFilter] = useState("active");
  const [actioningId, setActioningId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterExpertise) params.expertise = filterExpertise;
      // Batch #14.21 — Filtre archive
      if (archiveFilter === "archived") params.archived_only = true;
      else if (archiveFilter === "all") params.include_archived = true;
      const r = await axios.get(`${API}/masters`, { headers: authH(), params });
      setItems(r.data?.items || []);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [search, filterExpertise, archiveFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  // Batch #14.21 — Archive / Restore dans le tableau
  const handleArchive = async (m) => {
    if (!window.confirm(`Archiver le dossier "${m.couple_display_name || "(sans nom)"}" ?\n\nIl sera caché des listes et menus déroulants, mais conservé en base. Réactivable à tout moment depuis l'onglet "Archivés".`)) return;
    setActioningId(m.id);
    try {
      await axios.delete(`${API}/masters/${m.id}`, { headers: authH() });
      toast.success("Dossier archivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec d'archivage");
    } finally {
      setActioningId(null);
    }
  };

  const handleRestore = async (m) => {
    if (!window.confirm(`Réactiver le dossier "${m.couple_display_name || "(sans nom)"}" ?`)) return;
    setActioningId(m.id);
    try {
      await axios.post(`${API}/masters/${m.id}/restore`, {}, { headers: authH() });
      toast.success("Dossier réactivé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de réactivation");
    } finally {
      setActioningId(null);
    }
  };

  const headerCfg = HEADER_CONFIG[filterExpertise] || HEADER_CONFIG["null"];
  const HeaderIcon = headerCfg.icon;
  const isMasterRegistry = !filterExpertise;
  const detailBase = filterExpertise === "fiscal"
    ? "/admin/base-fiscale"
    : filterExpertise === "immeuble"
    ? "/admin/base-immeuble"
    : "/admin/masters";

  return (
    <div className="space-y-4" data-testid={`admin-masters-list${filterExpertise ? `-${filterExpertise}` : ""}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline-flex items-center gap-2">
            <HeaderIcon className={`w-6 h-6 ${headerCfg.iconColor}`} /> {headerCfg.title}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{headerCfg.subtitle}</p>
        </div>
        {/* Master Registry: créer un nouveau dossier · n° de client (comptabilité du cabinet) */}
        {isMasterRegistry && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/admin/comptabilite/clients"
              className="px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-sm inline-flex items-center gap-1.5 hover:bg-emerald-100 whitespace-nowrap"
              data-testid="masters-client-numbers-link"
              title="Numéros de client, ventes et soldes (comptabilité du cabinet)"
            >
              <Hash className="w-4 h-4" /> <span className="hidden sm:inline">N° de client</span>
            </Link>
            <button
              onClick={() => setShowCreate(true)}
              className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm inline-flex items-center gap-1.5 hover:bg-indigo-700 whitespace-nowrap"
              data-testid="masters-new-btn"
            >
              <Plus className="w-4 h-4" /> <span className="hidden sm:inline">Nouveau dossier Master</span><span className="sm:hidden">Nouveau</span>
            </button>
          </div>
        )}
        {/* Expertise: ajouter un dossier (sélection Master existant) */}
        {!isMasterRegistry && (
          <button
            onClick={() => setShowAddToExp(true)}
            className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm inline-flex items-center gap-1.5 hover:bg-emerald-700"
            data-testid="masters-add-to-exp-btn"
          >
            <Plus className="w-4 h-4" /> Ajouter un dossier à cette expertise
          </button>
        )}
      </div>

      {/* Bandeau info pour les expertises (consommatrices) */}
      {!isMasterRegistry && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 inline-flex items-start gap-2">
          <Star className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Pour ajouter un dossier ici, allez dans la tuile <Link to="/admin/masters" className="underline font-semibold">Master</Link> et activez l'expertise <b>{filterExpertise}</b> sur un dossier existant.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            placeholder="Rechercher…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border rounded-lg text-sm w-full"
            data-testid="masters-search-input"
          />
        </div>

        {/* Batch #14.21 — Onglets Actifs / Archivés / Tous */}
        <div className="inline-flex border rounded-lg p-0.5 bg-slate-50" role="group" data-testid="masters-archive-tabs">
          {[
            { id: "active", label: "Actifs", tone: "indigo" },
            { id: "archived", label: "Archivés", tone: "amber" },
            { id: "all", label: "Tous", tone: "gray" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setArchiveFilter(t.id)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${
                archiveFilter === t.id
                  ? `bg-white shadow-sm ${
                      t.tone === "amber" ? "text-amber-700" : t.tone === "indigo" ? "text-indigo-700" : "text-slate-700"
                    }`
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid={`masters-archive-filter-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tuiles (Batch #14.66 — vue tuile remplace la vue liste) */}
      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16">
          <Loader2 className="inline w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16 text-slate-400" data-testid="masters-empty">
          <FolderTree className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-sm">
            {filterExpertise
              ? `Aucun Master avec l'expertise ${filterExpertise} activée.`
              : "Aucun dossier Master. Cliquez sur \"Nouveau dossier Master\" pour démarrer."}
          </p>
        </div>
      ) : (
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4"
          data-testid="masters-tiles"
        >
          {items.map((m) => {
            const activeExps = (m.active_expertises || []).filter((k) => k !== "autonome" || !(m.active_expertises || []).includes("tenue_livres")).map((k) => (k === "autonome" ? "tenue_livres" : k));
            const accent = filterExpertise === "fiscal"
              ? { border: "hover:border-blue-400", iconBg: "bg-blue-50 border-blue-100", iconColor: "text-blue-600", link: "text-blue-600 hover:text-blue-800" }
              : filterExpertise === "immeuble"
              ? { border: "hover:border-emerald-400", iconBg: "bg-emerald-50 border-emerald-100", iconColor: "text-emerald-600", link: "text-emerald-600 hover:text-emerald-800" }
              : { border: "hover:border-indigo-400", iconBg: "bg-indigo-50 border-indigo-100", iconColor: "text-indigo-600", link: "text-indigo-600 hover:text-indigo-800" };
            const HeaderTileIcon = filterExpertise === "fiscal" ? FileText : filterExpertise === "immeuble" ? Building2 : Star;
            return (
              <div
                key={m.id}
                className={`group relative bg-white border border-slate-200 ${accent.border} hover:shadow-lg rounded-2xl p-4 transition-all duration-150 hover:-translate-y-0.5 ${m.archived ? "opacity-80" : ""}`}
                data-testid={`masters-tile-${m.id}`}
              >
                {/* Lien plein-tuile vers la fiche (overlay invisible, sous les boutons d'action) */}
                <Link
                  to={`${detailBase}/${m.id}`}
                  className="absolute inset-0 rounded-2xl z-0"
                  aria-label={`Ouvrir ${m.couple_display_name || "dossier"}`}
                  data-testid={`masters-tile-link-${m.id}`}
                />

                {/* En-tête : icône + badge archivé */}
                <div className="relative z-10 flex items-start justify-between mb-3 pointer-events-none">
                  <div className={`w-10 h-10 rounded-lg ${accent.iconBg} border flex items-center justify-center`}>
                    <HeaderTileIcon className={`w-5 h-5 ${accent.iconColor}`} />
                  </div>
                  {m.archived && (
                    <span
                      className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200 font-medium"
                      data-testid={`masters-archived-badge-${m.id}`}
                    >
                      Archivé
                    </span>
                  )}
                </div>

                {/* Nom du couple */}
                <div className="relative z-10 pointer-events-none">
                  <h3 className="font-semibold text-slate-800 text-sm leading-tight line-clamp-2 min-h-[2.5rem] group-hover:text-indigo-700 transition-colors">
                    {m.couple_display_name || <span className="text-slate-400 italic">(sans nom)</span>}
                  </h3>
                  <div className="text-[10px] text-slate-400 font-mono mt-0.5">
                    {m.client_number ? <span className="text-emerald-700 font-semibold" data-testid={`masters-client-number-${m.id}`}>N° {m.client_number}</span> : <span data-testid={`masters-client-number-${m.id}`}>{m.id.slice(0, 8)}…</span>}
                  </div>
                </div>

                {/* Membres */}
                <div className="relative z-10 mt-2.5 pointer-events-none">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600 truncate">
                    <Users className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    <span className="truncate">
                      {m.primary_name || "?"}
                      {m.secondary_name && <span className="text-slate-400"> + {m.secondary_name}</span>}
                    </span>
                  </div>
                  {m.primary_email && (
                    <div className="text-[11px] text-slate-400 mt-0.5 truncate">{m.primary_email}</div>
                  )}
                </div>

                {/* Expertises actives */}
                <div className="relative z-10 mt-3 flex flex-wrap gap-1 min-h-[22px] pointer-events-none">
                  {activeExps.length === 0 ? (
                    <span className="text-[10px] text-slate-400 italic">Aucune expertise</span>
                  ) : (
                    activeExps.map((k) => {
                      const Icon = EXPERTISE_ICON[k];
                      return (
                        <span
                          key={k}
                          className={`text-[10px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${EXPERTISE_COLOR[k] || "bg-slate-100 border-slate-200"}`}
                        >
                          {Icon && <Icon className="w-3 h-3" />} {EXPERTISE_LABEL[k] || k}
                        </span>
                      );
                    })
                  )}
                </div>

                {/* Pied : actions */}
                <div className="relative z-10 mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  {m.archived ? (
                    <button
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleRestore(m); }}
                      disabled={actioningId === m.id}
                      className="text-xs text-emerald-700 hover:text-emerald-900 hover:underline font-medium inline-flex items-center gap-1 disabled:opacity-50"
                      data-testid={`masters-restore-btn-${m.id}`}
                      title="Réactiver ce dossier"
                    >
                      {actioningId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
                      Réactiver
                    </button>
                  ) : (
                    <>
                      <Link
                        to={`${detailBase}/${m.id}`}
                        className={`text-xs ${accent.link} font-medium`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        Ouvrir →
                      </Link>
                      <button
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleArchive(m); }}
                        disabled={actioningId === m.id}
                        className="text-amber-600 hover:text-amber-800 disabled:opacity-50 p-1 rounded hover:bg-amber-50"
                        data-testid={`masters-archive-btn-${m.id}`}
                        title="Archiver ce dossier (le cacher des listes)"
                      >
                        {actioningId === m.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Archive className="w-3.5 h-3.5" />}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateMasterModal onClose={() => setShowCreate(false)} onCreated={load} />}
      {showAddToExp && (
        <AddToExpertiseModal
          expertise={filterExpertise}
          existingMasterIds={items.map((m) => m.id)}
          onClose={() => setShowAddToExp(false)}
          onAdded={load}
        />
      )}
    </div>
  );
}
